import { audio } from '../systems/audio/AudioSystem.js';
import { LeaderboardClient } from '../systems/net/LeaderboardClient.js';
import { RepriseClient, calcResonance } from '../systems/net/RepriseClient.js';
import { BOSSES } from '../data/bosses.js';
import { ACT_EPILOGUES } from '../data/lore.js';
import { formatTime } from '../core/formatTime.js';
import { seedLabel } from '../data/seeds.js';

/** Run-over screen: win/loss readout, Echoes earned, survival time, and a way back to the main menu. */
export class RunEndOverlay {
  constructor(eventBus, root, { onReturnToMenu, onContinueEndless, save }) {
    this.root = root;
    this.onReturnToMenu = onReturnToMenu;
    this.onContinueEndless = onContinueEndless;
    this.save = save;
    eventBus.on('run:ended', (payload) => this._show(payload));
  }

  _show({ runId, seed = null, won, echoes, shards, glimmer = 0, newlyUnlockedCosmetics = [], newlyUnlockedAchievements = [], survivalTime, totalDamageDealt, bossName, characterName, leaderboardScore, scoreBreakdown, gauntletIndex = -1, ascensionLevel = 0, endless = false, challenge = false, noEchoUpgrades = false, noShardPerks = false, noPetBonuses = false, damageRecap = null, captures = 0, captureAttempts = 0, practice = false, actId = 'act1', board = 'act1' }) {
    // Which act's ending this run earned. All three are standalone acts now
    // (data/acts.js), so each clear is a real ending — its own. Act I's is the
    // King's fall, which used to be a mid-run threshold rather than a finish.
    const epilogue = ACT_EPILOGUES[actId] ?? ACT_EPILOGUES.act1;
    // Boss Challenge is the surviving rewardless mode; `practice` is still
    // honoured for any route that sets it (data/acts.js). Both skip the score,
    // payout, leaderboard and Reprise blocks below.
    const rewardless = challenge || practice;
    const el = document.createElement('div');
    el.className = 'runend-overlay';
    el.innerHTML = `
      <div class="runend-title">${this._title({ won, challenge, actId })}</div>
      ${won && !endless && !challenge ? `<div class="runend-epilogue">${epilogue}</div>` : ''}
      ${practice ? '<div class="runend-practice-note">Practice route — no Echoes, Shards, or leaderboard entry.</div>' : ''}
      ${rewardless ? '' : `
      <div class="runend-score-label">FINAL SCORE</div>
      <div class="runend-score">${leaderboardScore}</div>
      ${this._renderScoreBreakdown(scoreBreakdown)}
      <div class="runend-reward-panel"><div class="runend-time">Ascension ${ascensionLevel}${endless ? ' · Endless' : ''}</div><div class="runend-echoes">+${echoes} Echoes</div>${shards > 0 ? `<div class="runend-shards">+${shards} Sovereign Shards</div>` : ''}${glimmer > 0 ? `<div class="runend-glimmer">+${glimmer} Glimmer</div>` : ''}</div>
      ${newlyUnlockedCosmetics.length > 0 ? `<div class="runend-unlocked">Cosmetic unlocked: ${newlyUnlockedCosmetics.join(', ')}</div>` : ''}
      `}
      ${/* Outside the rewardless block, unlike the Echo/cosmetic panel above:
            achievements are granted in EVERY mode (Game calls
            evaluateAchievements unconditionally), so hiding them in Boss
            Challenge and practice runs would pay a reward the player never
            sees arrive. */''}
      ${this._renderAchievements(newlyUnlockedAchievements)}
      <div class="runend-time">Survived ${formatTime(survivalTime)} vs. ${bossName}</div>
      ${/* Seeded runs (BULLET-HELL.md §D8). Shown in every mode, including the
            rewardless ones, because the seed is what makes the run repeatable
            and shareable — a player who wants to hand this run to someone else
            needs to be able to read it off the screen they end on. */''}
      ${seed ? `<div class="runend-seed">Seed: <strong>${seedLabel(seed)}</strong></div>` : ''}
      ${/* Spell cards captured (BULLET-HELL.md §D1). Outside the rewardless
            block for the same reason as the achievements above: cards are
            judged and recorded in every mode, and the practice modes are where
            a player goes specifically to raise this line. */''}
      ${captureAttempts > 0 ? `<div class="runend-captures">Spell cards captured: <strong>${captures}</strong> / ${captureAttempts}</div>` : ''}
      ${this._renderDamageRecap(damageRecap, won)}
      ${rewardless ? '' : '<div class="runend-leaderboard-status"></div>'}
      ${rewardless ? '' : '<div class="runend-reprise-status"></div>'}
    `;
    // Endless loops Act I, so it is only offered where Game passes the callback
    // at all — never off a rewardless run, which has forgone those payouts.
    if (won && this.onContinueEndless && !rewardless) {
      const endlessButton = document.createElement('button');
      endlessButton.type = 'button';
      endlessButton.className = 'menu-button menu-button-primary';
      endlessButton.textContent = 'Continue to Endless';
      endlessButton.addEventListener('click', () => {
        audio.playSfx('click');
        el.remove();
        this.onContinueEndless();
      });
      el.appendChild(endlessButton);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'menu-button menu-button-primary';
    button.textContent = 'Return to Menu';
    button.addEventListener('click', () => {
      audio.playSfx('click');
      el.remove();
      this.onReturnToMenu();
    });
    el.appendChild(button);
    this.root.appendChild(el);

    if (rewardless) return; // practice — no leaderboard submission, no Reprise contribution
    const name = this.save.accountUsername || this.save.profileName;
    this._submitScore(el, {
      runId,
      // Which seed this row played, or null for an ordinary run — the board's
      // daily view is exactly "the rows that share today's seed", so a row that
      // didn't carry it could never appear on it (BULLET-HELL.md §D8).
      seed,
      name,
      score: leaderboardScore,
      time: survivalTime,
      boss: bossName,
      character: characterName,
      won,
      ascension: ascensionLevel,
      endless,
      gauntletIndex,
      bossIndex: gauntletIndex >= 0 ? gauntletIndex % BOSSES.length : -1,
      // Which ladder this row belongs on. Each act starts at a different index
      // and the score's progress term is start-relative, so the acts cannot
      // share one board without lying about progress (data/acts.js).
      board,
      noEchoUpgrades,
      noShardPerks,
      noPetBonuses,
    });

    // Reprise (REPRISE.md phase R0): pour this run's Resonance into the
    // world's open front. Signed-out players and seasons that aren't running
    // both no-op silently — the RPC needs auth.uid() and an open district, and
    // RepriseClient swallows the absence of either.
    //
    // Taken from the score breakdown rather than recomputed as
    // `gauntletIndex + won`: that shortcut assumes a run began at boss one, and
    // an Act III run begins at fourteen, so it would credit thirteen fights
    // that never happened. `bossesCleared` is already start-relative.
    const bossesDefeated = scoreBreakdown?.bossesCleared ?? (gauntletIndex + (won ? 1 : 0));
    // Each act contributes at its own run-worth (data/repriseTuning.js): the
    // deep acts are six harder bosses in ~17 minutes and Act I is seven easier
    // ones in ~19, so a single per-boss rate would misprice one of them.
    const resonance = calcResonance({ bossesDefeated, survivalSeconds: survivalTime, ascensionLevel, actId });
    this._contributeResonance(el, { runId, amount: resonance });
  }

  /**
   * Run-over headline. An act-select run is still a run through that act, so
   * it's named for the act rather than borrowed from Boss Challenge's
   * single-fight copy — "Challenge Failed" after six Undersong bosses would
   * read as the wrong mode entirely.
   *
   * There used to be a third branch here, for a `practice` act's own name. No
   * ACTS entry has set `practice: true` since Act II became rewarded (see
   * acts.js, which keeps the flag as a contract for Boss Challenge and future
   * acts), and Boss Challenge takes the `challenge` branch above — so the
   * branch, and the `actName` Game sent to feed it, were dead on both sides.
   */
  _title({ won, challenge, actId }) {
    if (challenge) return won ? 'Boss Defeated' : 'Challenge Failed';
    // Each act gets its own headline: only Act I is a gauntlet, and "Gauntlet
    // Cleared" after six Undersong bosses reads as the wrong run entirely.
    if (!won) return 'You Fell';
    if (actId === 'act2') return 'The Undersong Silenced';
    if (actId === 'act3') return 'The Anthem Finished';
    return 'Gauntlet Cleared';
  }

  /**
   * Achievements earned by this run (BULLET-HEAVEN.md §H6 — machine #8).
   *
   * Sits on the run-end screen rather than only in the statistics menu
   * because that is the screen a player is looking at when they decide
   * whether to press retry. A loss that names two things you just achieved
   * and pays Echoes for them is a different proposition from a loss that
   * shows a number going up.
   *
   * The Echo total is spelled out per achievement: the payout has already
   * been banked by SaveManager, and a reward the player can't see arriving
   * may as well not have been paid.
   */
  _renderAchievements(achievements) {
    if (!achievements || achievements.length === 0) return '';
    const rows = achievements.map(({ name, hint, echoes }) => `
      <div class="runend-achievement">
        <span class="runend-achievement-name">${name}</span>
        <span class="runend-achievement-hint">${hint}</span>
        ${echoes ? `<span class="runend-achievement-echoes">+${echoes}</span>` : ''}
      </div>
    `).join('');
    return `
      <div class="runend-achievements">
        <div class="runend-section-heading">${achievements.length === 1 ? 'Achievement' : 'Achievements'}</div>
        ${rows}
      </div>
    `;
  }

  /**
   * Death recap (ARCHIVE.md P4): what actually hurt this run. A death used to
   * teach nothing — with thirteen bosses' worth of patterns there was no way
   * to identify the one that killed you. Names the killing blow first (the
   * lesson), then the damage-taken-by-source table (the pattern behind it).
   *
   * On a win it's a "what nearly got you" readout instead, so it's still
   * worth reading after a clear. A flawless run passes a null recap and shows
   * nothing at all.
   */
  _renderDamageRecap(recap, won) {
    if (!recap || !recap.rows.length) return '';
    // Long tails aren't a lesson — after five sources the rows stop naming a
    // cause and start being a wall of chip damage. The remainder is folded
    // into one honest "everything else" row so the numbers still add up.
    const TOP_N = 5;
    const shown = recap.rows.slice(0, TOP_N);
    const restDamage = recap.rows.slice(TOP_N).reduce((sum, row) => sum + row.damage, 0);
    const worst = shown[0].damage || 1;
    const row = (label, damage, hits) => `
      <div class="runend-recap-row">
        <span class="runend-recap-name">${label}</span>
        <span class="runend-recap-bar"><i style="width:${Math.round((damage / worst) * 100)}%"></i></span>
        <span class="runend-recap-damage">${damage}${hits != null ? ` <small>&times;${hits}</small>` : ''}</span>
      </div>`;
    const rows = shown.map((r) => row(r.label, r.damage, r.hits)).join('')
      + (restDamage > 0 ? row('Everything else', restDamage, null) : '');
    return `
      <div class="runend-recap">
        <div class="runend-recap-label">${won ? 'DAMAGE TAKEN' : 'WHAT KILLED YOU'}</div>
        ${!won && recap.killedBy ? `<div class="runend-recap-killer">Final blow: <strong>${recap.killedBy}</strong></div>` : ''}
        ${rows}
      </div>`;
  }

  _renderScoreBreakdown(breakdown) {
    if (!breakdown) return '';
    // The score is (progress + performance) * difficulty * purity, so the
    // readout is split: additive rows first, then the multipliers that scaled
    // them. Multiplier rows are only shown when they actually did something —
    // a plain Normal run would otherwise be a wall of "x1".
    const bosses = breakdown.bossesCleared === 1 ? '1 boss' : `${breakdown.bossesCleared} bosses`;
    const rows = [[`Bosses cleared (${bosses})`, `+${breakdown.bossCredit}`]];
    if (breakdown.clearBonus) rows.push(['Gauntlet clear', `+${breakdown.clearBonus}`]);
    rows.push([breakdown.performanceKind === 'speed' ? 'Speed' : 'Survival', `+${breakdown.performanceCredit}`]);
    if (breakdown.ascensionMultiplier !== 1) rows.push(['Ascension', `x${breakdown.ascensionMultiplier.toFixed(1)}`]);
    if (breakdown.endlessMultiplier !== 1) rows.push(['Endless depth', `x${breakdown.endlessMultiplier.toFixed(2)}`]);
    if (breakdown.purityMultiplier !== 1) rows.push(['Unaided', `x${breakdown.purityMultiplier.toFixed(2)}`]);
    return `<div class="runend-score-breakdown">${rows.map(([label, value]) => `<div><span>${label}</span><strong>${value}</strong></div>`).join('')}</div>`;
  }

  async _submitScore(el, entry) {
    if (!entry.name) return; // no account and no guest display name set — leaderboard is opt-in
    const status = el.querySelector('.runend-leaderboard-status');
    if (!status) return; // overlay was dismissed before the request resolved
    status.textContent = 'Submitting to leaderboard…';
    const result = await LeaderboardClient.submit(entry);
    const stillMounted = el.isConnected && el.querySelector('.runend-leaderboard-status');
    if (!stillMounted) return;
    status.textContent = result.ok
      ? (result.rank ? `Ranked #${result.rank} on the leaderboard!` : 'Submitted to the leaderboard.')
      : "Couldn't reach the leaderboard — score saved locally only.";
  }

  async _contributeResonance(el, { runId, amount }) {
    if (!(amount > 0)) return;
    const status = el.querySelector('.runend-reprise-status');
    if (!status) return; // overlay dismissed before we got here
    // Dispatches to finale damage during R5's finale, else Resonance to the front.
    const result = await RepriseClient.contributeRun({ amount, runId });
    const stillMounted = el.isConnected && el.querySelector('.runend-reprise-status');
    if (!stillMounted) return;
    if (!result.ok) return; // no active season / signed out / offline — stay silent, it's opt-in

    if (result.mode === 'finale') {
      if (result.duplicate) {
        status.textContent = 'Reprise: this run already struck the King.';
      } else if (result.defeated) {
        status.textContent = 'Reprise: the Unmade King falls — Melodia stands again!';
      } else if (result.dealt > 0) {
        status.textContent = `Reprise: dealt ${result.dealt} to the Unmade King.`;
      } else {
        status.textContent = 'Reprise: daily finale damage cap reached.';
      }
      return;
    }

    // Resonance mode (R0).
    if (result.duplicate) {
      status.textContent = `Reprise: already counted toward ${result.district?.label ?? 'the world'}.`;
      return;
    }
    const where = result.district?.label ?? 'the world';
    status.textContent = result.granted > 0
      ? `Reprise: +${result.granted} Resonance to ${where}.`
      : `Reprise: daily Resonance cap reached — nothing added to ${where}.`;
  }
}
